/* eslint-disable no-nested-ternary */
import { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import DecoratorWorkOrderShipTo from "pages/order/DecoratorWorkOrderShipTo";
import DecoratorWorkOrderGarmentData from "pages/order/DecoratorWorkOrderGarmentData";
import DecoratorWorkOrderLineItems from "pages/order/DecoratorWorkOrderLineItems";
import DecoratorOtherCharges from "pages/order/DecoratorOtherCharges";
import { usePost } from "hooks/usePost";
import { PROP_TYPES, DEFAULT_STATE } from "pages/order/prop-types";
import { BOT_CA_ADDRESS, BOT_USA_ADDRESS } from "constants/address";
import LOGO_BASE64 from "pages/bot-network/SalesReportDialog/logo";
import { CONFLUENCE_ORDERS } from "constants/services";
import {
  DecoratortWorkDialogWrapper,
  PoSecureUrl,
  WorkOrderWrapper,
  WorkOrderHeader,
  WorkOrderTitle,
  SubmitButton,
} from "styles/pages/order";
import {
  PDFDownloadLink,
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Image,
  pdf,
} from "@react-pdf/renderer";
import moment from "moment";
import { useSelector } from "react-redux";
import AlertDialog from "components/AlertDialog";

const styles = StyleSheet.create({
  page: {
    padding: 30,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  companyDetails: {
    flex: 1,
  },
  companyName: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 5,
  },
  companyAddress: {
    fontSize: 10,
    marginBottom: 2,
  },
  logo: {
    width: 150,
    height: 50,
    objectFit: "contain",
  },
  section: {},
  title: {
    fontSize: 14,
    marginBottom: 10,
    textAlign: "center",
  },
  subtitle: {
    fontSize: 12,
    marginBottom: 10,
  },
  table: {
    display: "flex",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#bfbfbf",
    marginBottom: 20,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#bfbfbf",
    borderBottomStyle: "solid",
  },
  tableHeader: {
    backgroundColor: "#f0f0f0",
    padding: 5,
    flex: 1,
    textAlign: "center",
    fontWeight: "bold",
    fontSize: 12,
  },
  tableCell: {
    padding: 5,
    flex: 1,
    textAlign: "center",
    fontSize: 10,
  },
  total: {
    marginTop: 20,
    textAlign: "right",
  },
  totalText: {
    fontSize: 16,
    fontWeight: "bold",
  },
  shipToInfo: {},
  shipToText: {
    fontSize: 10,
    marginBottom: 20,
    fontWeight: "bold",
  },
  garmentDetails: {
    marginTop: 6,
  },
  garmentInfo: {},
  garmentText: {
    fontSize: 10,
    marginBottom: 5,
  },
  garmentLabel: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 2,
  },
  garmentValue: {
    fontSize: 10,
    marginBottom: 5,
  },
});

const DecoratorWorkOrderDialog = ({ state, setState, fetchOrder }) => {
  const user = useSelector((store) => store?.user);
  const { mutateAsync } = usePost();
  const [decoratorData, setDecoratorData] = useState();
  const [showCopyConfirm, setShowCopyConfirm] = useState(false);

  const printProvider = state?.printProvider?.id;
  const address = printProvider === "CA" ? BOT_CA_ADDRESS : BOT_USA_ADDRESS;
  const selectedDecorator = state?.selectedDecorator;
  const generateEstimation = state?.generateEstimation;
  const generatePackingSlip = state?.generatePackingSlip;
  const createInvoice = state?.createInvoice;

  let estimateData = state?.manualEstimateData?.generateEstimation
    ? state?.manualEstimateData
    : state?.estimateData;

  if (createInvoice) {
    estimateData = state?.manualInvoiceData;
  } else if (generatePackingSlip) {
    estimateData = state?.manualPackingSlip;
  }

  const shipAddress = estimateData?.shipAddress;

  const handleClose = () => {
    setState({
      ...state,
      selectedDecorator: undefined,
      generateEstimation: false,
      generatePackingSlip: false,
      createInvoice: false,
    });
  };

  const initDecoratorData = () => {
    setDecoratorData({
      selectedDecorator,
      generateEstimation,
      garmentSentDate: state?.garmentSentDate || estimateData?.garmentSentDate,
      orderDueDate: state?.orderDueDate || estimateData?.orderDueDate,
      trackingNumber: state?.trackingNumber || estimateData?.trackingNumber,
      shipAddress,
      otherCharges: estimateData?.otherCharges || [],
      lineItems:
        estimateData?.lineItems?.map((l) => {
          const decorationCost = state?.decorations[0]?.value === "EMB" ? 9 : 0;

          return {
            ...l,
            chargeAmount:
              state?.decorations[0]?.value === "EMB"
                ? decorationCost * l.qty
                : 0,
            decorationCost,
            decoration:
              state?.decorations?.length > 1
                ? undefined
                : {
                    id: state?.decorations[0]?.value,
                    label: state?.decorations[0]?.value,
                  },
          };
        }) || [],
    });
  };

  useEffect(() => {
    if (!decoratorData) {
      if (!state?.decoratorData) {
        initDecoratorData();
      } else {
        setDecoratorData(state?.decoratorData);
      }
    }
  }, [decoratorData, state]);

  const submitDisabled =
    !decoratorData?.shipAddress?.shipName?.trim() ||
    !decoratorData?.shipAddress?.address1?.trim() ||
    !decoratorData?.shipAddress?.city?.trim() ||
    !decoratorData?.shipAddress?.state?.trim() ||
    !decoratorData?.shipAddress?.country?.trim() ||
    !decoratorData?.shipAddress?.zip?.trim() ||
    !!decoratorData?.lineItems?.find(
      (l) =>
        !l?.description?.trim() ||
        !l?.qty ||
        !l?.color?.trim() ||
        !l?.size?.trim() ||
        !l?.decoration ||
        !l?.decorationCost
    ) ||
    !!decoratorData?.otherCharges?.find(
      (l) => !l?.description?.trim() || !l?.qty || !l?.unitCost
    );

  const save = async () => {
    let payload = {
      vendorName: state?.vendorName,
      orderNumber: state?.orderNumber,
      submitToDecorator: !!state?.submitToDecorator,
      decoratorData: {
        ...decoratorData,
        lineItems: decoratorData.lineItems?.map((l) => ({
          ...l,
          additionalInformation: l?.additionalInformation || "",
        })),
      },
    };

    if (generateEstimation) {
      payload = {
        vendorName: state?.vendorName,
        orderNumber: state?.orderNumber,
        manualEstimateData: {
          ...decoratorData,
          lineItems: decoratorData.lineItems?.map((l) => ({
            ...l,
            additionalInformation: l?.additionalInformation || "",
          })),
        },
      };
    }

    if (generatePackingSlip) {
      payload = {
        vendorName: state?.vendorName,
        orderNumber: state?.orderNumber,
        manualPackingSlip: {
          ...decoratorData,
          lineItems: decoratorData.lineItems?.map((l) => ({
            ...l,
            additionalInformation: l?.additionalInformation || "",
          })),
        },
      };
    }

    if (createInvoice) {
      payload = {
        vendorName: state?.vendorName,
        orderNumber: state?.orderNumber,
        manualInvoiceData: {
          ...decoratorData,
          lineItems: decoratorData.lineItems?.map((l) => ({
            ...l,
            additionalInformation: l?.additionalInformation || "",
          })),
        },
      };
    }

    await mutateAsync({
      url: CONFLUENCE_ORDERS,
      data: payload,
      isPatch: true,
    });

    if (fetchOrder) {
      await fetchOrder();
    }

    handleClose();
  };

  const createEstimationPdf = () => {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <View style={styles.header}>
            <View style={styles.companyDetails}>
              <Text style={styles.companyName}>{address?.comapnyName}</Text>
              <Text style={styles.companyAddress}>
                {address?.addressLineOne}
              </Text>
              <Text style={styles.companyAddress}>
                {address?.city} {address?.state} {address?.postalCode}
              </Text>
              <Text style={styles.companyAddress}>{address?.country}</Text>
              <Text style={styles.companyAddress}>{address?.email}</Text>
            </View>

            <Image style={styles.logo} src={LOGO_BASE64} />
          </View>
          <View style={styles.section}>
            <Text style={styles.title}>
              {createInvoice
                ? "Invoice"
                : generateEstimation
                ? "Estimate"
                : "Packing Slip"}
            </Text>

            <View>
              <Text style={styles.subtitle}>Ship To</Text>

              <View style={styles.shipToInfo}>
                <Text style={[styles.shipToText, { marginBottom: 0 }]}>
                  {[decoratorData?.shipAddress?.shipName]
                    .filter(Boolean)
                    .join(", ")}
                </Text>
              </View>
              <View style={styles.shipToInfo}>
                <Text style={[styles.shipToText, { marginBottom: 0 }]}>
                  {[
                    decoratorData?.shipAddress?.address1,
                    decoratorData?.shipAddress?.address2,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </Text>
              </View>
              <View style={styles.shipToInfo}>
                <Text style={[styles.shipToText, { marginBottom: 0 }]}>
                  {[
                    `${decoratorData?.shipAddress?.city}, ${decoratorData?.shipAddress?.state} ${decoratorData?.shipAddress?.zip}`,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </Text>
              </View>
              <View style={styles.shipToInfo}>
                <Text style={styles.shipToText}>
                  {[decoratorData?.shipAddress?.country]
                    .filter(Boolean)
                    .join(", ")}
                </Text>
              </View>
            </View>

            <View style={styles.garmentDetails}>
              <Text style={styles.subtitle}>Garment Details</Text>
              <View style={styles.garmentInfo}>
                <Text style={styles.garmentText}>
                  Garment Sent Date:{" "}
                  {decoratorData?.garmentSentDate
                    ? moment
                        .unix(decoratorData.garmentSentDate)
                        .format("MMM DD, YYYY")
                    : "N/A"}
                </Text>
                <Text style={styles.garmentText}>
                  Order Due Date:{" "}
                  {decoratorData?.orderDueDate
                    ? moment
                        .unix(decoratorData.orderDueDate)
                        .format("MMM DD, YYYY")
                    : "N/A"}
                </Text>
                {decoratorData?.trackingNumber && (
                  <Text style={styles.garmentText}>
                    Tracking Number: {decoratorData.trackingNumber}
                  </Text>
                )}
              </View>
            </View>

            <Text style={[styles.subtitle, { marginTop: 20 }]}>Line Items</Text>
            <View style={styles.table}>
              <View style={styles.tableRow}>
                <Text style={styles.tableHeader}>Item</Text>
                <Text style={styles.tableHeader}>Color</Text>
                <Text style={styles.tableHeader}>Size</Text>
                <Text style={styles.tableHeader}>Decoration</Text>
                <Text style={styles.tableHeader}>Quantity</Text>
                {(generateEstimation || createInvoice) && (
                  <Text style={styles.tableHeader}>Price</Text>
                )}
                {(generateEstimation || createInvoice) && (
                  <Text style={styles.tableHeader}>Total</Text>
                )}
              </View>
              {decoratorData?.lineItems?.map((item) => (
                <>
                  <View
                    key={`${item.description}-${item.qty}-${item.chargeAmount}`}
                    style={[
                      styles.tableRow,
                      { borderBottomWidth: item.additionalInformation ? 0 : 1 },
                    ]}
                  >
                    <Text style={styles.tableCell}>{item.description}</Text>
                    <Text style={styles.tableCell}>{item.color}</Text>
                    <Text style={styles.tableCell}>{item.size}</Text>
                    <Text style={styles.tableCell}>
                      {item.decoration?.label}
                    </Text>
                    <Text style={styles.tableCell}>{item.qty}</Text>
                    {(generateEstimation || createInvoice) && (
                      <Text style={styles.tableCell}>${item.chargeAmount}</Text>
                    )}
                    {(generateEstimation || createInvoice) && (
                      <Text style={styles.tableCell}>
                        ${(item.qty * item.chargeAmount).toFixed(2)}
                      </Text>
                    )}
                  </View>
                  {item.additionalInformation && (
                    <View style={styles.tableRow}>
                      <Text
                        style={[
                          styles.tableCell,
                          {
                            flex: 7,
                            fontStyle: "italic",
                            textAlign: "left",
                            borderBottomWidth: 0,
                          },
                        ]}
                      >
                        {" "}
                        {/* flex: 7 to span all columns */}
                        Decoration Notes: {item.additionalInformation}
                      </Text>
                    </View>
                  )}
                </>
              ))}
            </View>

            {decoratorData?.otherCharges?.length > 0 && (
              <>
                <Text style={[styles.subtitle, { marginTop: 20 }]}>
                  Other Charges
                </Text>
                <View style={styles.table}>
                  <View style={styles.tableRow}>
                    <Text style={styles.tableHeader}>Item</Text>

                    <Text style={styles.tableHeader}>Quantity</Text>
                    {(generateEstimation || createInvoice) && (
                      <Text style={styles.tableHeader}>Unit Cost</Text>
                    )}
                    {(generateEstimation || createInvoice) && (
                      <Text style={styles.tableHeader}>Total</Text>
                    )}
                  </View>
                  {decoratorData?.otherCharges?.map((item) => (
                    <View
                      key={`${item.description}-${item.qty}-${item.unitCost}`}
                      style={styles.tableRow}
                    >
                      <Text style={styles.tableCell}>{item.description}</Text>

                      <Text style={styles.tableCell}>{item.qty}</Text>
                      {(generateEstimation || createInvoice) && (
                        <Text style={styles.tableCell}>${item.unitCost}</Text>
                      )}
                      {(generateEstimation || createInvoice) && (
                        <Text style={styles.tableCell}>
                          ${(item.qty * item.unitCost).toFixed(2)}
                        </Text>
                      )}
                    </View>
                  ))}
                </View>
              </>
            )}

            {(generateEstimation || createInvoice) && (
              <View style={[styles.total]}>
                <Text style={[styles.totalText, { fontSize: 14 }]}>
                  Total Amount: $
                  {(
                    (decoratorData?.lineItems || []).reduce(
                      (sum, item) => sum + (item.qty * item.chargeAmount || 0),
                      0
                    ) +
                    (decoratorData?.otherCharges || []).reduce(
                      (sum, item) => sum + (item.qty * item.unitCost || 0),
                      0
                    )
                  ).toFixed(2)}
                </Text>
              </View>
            )}
          </View>
        </Page>
      </Document>
    );
  };

  return (
    <Dialog
      fullScreen
      open={
        !!selectedDecorator ||
        !!generateEstimation ||
        !!generatePackingSlip ||
        createInvoice
      }
      onClose={handleClose}
    >
      <AppBar sx={{ position: "relative" }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>

            {generateEstimation && (
              <Typography sx={{ flex: 1, m: 0 }} variant="h6" component="div">
                {state?.estimationUrl
                  ? "Update Estimation"
                  : "Generate Estimation"}
                &nbsp;({state?.orderNumber})
              </Typography>
            )}

            {generatePackingSlip && (
              <Typography sx={{ m: 0, flex: 1 }} variant="h6" component="div">
                {state?.packingSlipUrl
                  ? "Update Packing Slip"
                  : "Generate Packing Slip"}
                &nbsp;({state?.orderNumber})
              </Typography>
            )}

            {!generateEstimation && !generatePackingSlip && !createInvoice && (
              <Typography sx={{ m: 0, flex: 1 }} variant="h6" component="div">
                Work Order for Decorator ({selectedDecorator?.companyName})
              </Typography>
            )}

            {createInvoice && (
              <Typography sx={{ m: 0, flex: 1 }} variant="h6" component="div">
                Invoice ({state?.orderNumber})
              </Typography>
            )}
          </div>

          {(generatePackingSlip || createInvoice) && state?.manualEstimateData && (
            <div>
              <Button
                color="inherit"
                onClick={() => {
                  setShowCopyConfirm(true);
                }}
              >
                Copy Estimate Data
              </Button>
            </div>
          )}

          <div>
            <Button color="inherit" onClick={() => save()}>
              save
            </Button>

            {(generateEstimation || generatePackingSlip || createInvoice) && (
              <PDFDownloadLink
                document={createEstimationPdf()}
                fileName={
                  createInvoice
                    ? `Invoice(${state?.orderNumber}).pdf`
                    : generateEstimation
                    ? `Estimation(${state?.orderNumber}).pdf`
                    : `Packing Slip(${state?.orderNumber}).pdf`
                }
              >
                {({ loading }) => (
                  <Button
                    style={{ color: "white" }}
                    disabled={loading}
                    onClick={async () => save()}
                  >
                    {loading ? "Generating PDF..." : "Download PDF"}
                  </Button>
                )}
              </PDFDownloadLink>
            )}

            {(generateEstimation || generatePackingSlip) && (
              <Button
                style={{ color: "white" }}
                onClick={async () => {
                  const file = await pdf(createEstimationPdf()).toBlob();

                  const API_ENDPOINT =
                    "https://api.cloudinary.com/v1_1/big-oven-tees-inc/upload";

                  const fileData = new FormData();

                  fileData.append(
                    "file",
                    file,
                    generateEstimation
                      ? `Estimation(${state?.orderNumber}).pdf`
                      : `PackingSlip(${state?.orderNumber}).pdf`
                  );

                  fileData.append("upload_preset", "tnedst8q");

                  if (generateEstimation) {
                    fileData.append(
                      "folder",
                      `ConfluenceApp/Users/${user?.email}/Orders/${state?.orderNumber}/Estimation`
                    );
                  } else if (generatePackingSlip) {
                    fileData.append(
                      "folder",
                      `ConfluenceApp/Users/${user?.email}/Orders/${state?.orderNumber}/PackingSlips`
                    );
                  }

                  const data = await fetch(API_ENDPOINT, {
                    method: "post",
                    body: fileData,
                  });

                  const json = await data?.json();

                  if (generateEstimation) {
                    await mutateAsync({
                      url: CONFLUENCE_ORDERS,
                      data: {
                        vendorName: state?.vendorName,
                        orderNumber: state?.orderNumber,
                        estimationUrl: json?.secure_url,
                      },
                      isPatch: true,
                    });
                  } else if (generatePackingSlip) {
                    await mutateAsync({
                      url: CONFLUENCE_ORDERS,
                      data: {
                        vendorName: state?.vendorName,
                        orderNumber: state?.orderNumber,
                        packingSlipUrl: json?.secure_url,
                      },
                      isPatch: true,
                    });
                  }

                  if (fetchOrder) {
                    await fetchOrder();
                  }
                }}
              >
                Save and Upload
              </Button>
            )}

            {!state?.submitToDecorator && selectedDecorator && (
              <SubmitButton
                disabled={submitDisabled}
                onClick={async () => {
                  const payload = {
                    vendorName: state?.vendorName,
                    orderNumber: state?.orderNumber,
                    submitToDecorator: true,
                    decoratorData: {
                      ...decoratorData,
                      lineItems: decoratorData.lineItems?.map((l) => ({
                        ...l,
                        additionalInformation: l?.additionalInformation || "",
                      })),
                    },
                  };

                  await mutateAsync({
                    url: CONFLUENCE_ORDERS,
                    data: payload,
                    isPatch: true,
                  });

                  window.location.reload();
                }}
              >
                submit
              </SubmitButton>
            )}
          </div>
        </Toolbar>
      </AppBar>
      <DecoratortWorkDialogWrapper>
        <PoSecureUrl>
          <iframe src={state?.poSecureUrl} title="po-secure-url" />

          {selectedDecorator && (
            <iframe src={state?.artProof?.artProofUrl} title="po-secure-url" />
          )}
        </PoSecureUrl>
        <WorkOrderWrapper>
          <WorkOrderHeader>
            <div className="address">
              <div>{address?.comapnyName}</div>
              <div className="company-address">{address?.addressLineOne}</div>
              <div className="company-location">
                {address?.city} {address?.state} {address?.postalCode}
              </div>
              <div className="company-email">{address?.email}</div>
            </div>
            <div className="logo">
              <img src={address?.companyLogoUrl} alt="logo" />
            </div>
          </WorkOrderHeader>

          <WorkOrderTitle>
            {generateEstimation ? "Generate Estimate" : "Work Order"}
          </WorkOrderTitle>

          <DecoratorWorkOrderGarmentData
            decoratorData={decoratorData}
            setDecoratorData={setDecoratorData}
          />

          <DecoratorWorkOrderShipTo
            decoratorData={decoratorData}
            setDecoratorData={setDecoratorData}
          />

          <DecoratorWorkOrderLineItems
            decoratorData={decoratorData}
            setDecoratorData={setDecoratorData}
            state={state}
          />

          <DecoratorOtherCharges
            decoratorData={decoratorData}
            setDecoratorData={setDecoratorData}
            state={state}
          />
        </WorkOrderWrapper>
      </DecoratortWorkDialogWrapper>
      <AlertDialog
        open={showCopyConfirm}
        title="Copy Estimate Data?"
        description="This will overwrite any changes in the packing slip section with data from the estimate. Are you sure you want to proceed? This can't be undone."
        agreeTitle="Yes"
        disagreeTitle="No"
        onAgree={() => {
          const estimateData1 =
            state?.manualEstimateData || state?.estimateData;
          setState({
            ...state,
            manualPackingSlip: estimateData1,
          });

          setDecoratorData({
            selectedDecorator,
            generateEstimation,
            garmentSentDate:
              state?.garmentSentDate || estimateData1?.garmentSentDate,
            orderDueDate: state?.orderDueDate || estimateData1?.orderDueDate,
            trackingNumber:
              state?.trackingNumber || estimateData1?.trackingNumber,
            shipAddress: estimateData1?.shipAddress,
            otherCharges: estimateData1?.otherCharges || [],
            lineItems:
              estimateData1?.lineItems?.map((l) => {
                const decorationCost =
                  state?.decorations[0]?.value === "EMB" ? 9 : 0;
                return {
                  ...l,
                  chargeAmount:
                    state?.decorations[0]?.value === "EMB"
                      ? decorationCost * l.qty
                      : 0,
                  decorationCost,
                  decoration:
                    state?.decorations?.length > 1
                      ? undefined
                      : {
                          id: state?.decorations[0]?.value,
                          label: state?.decorations[0]?.value,
                        },
                };
              }) || [],
          });
          setShowCopyConfirm(false);
        }}
        onDisagree={() => setShowCopyConfirm(false)}
      />
    </Dialog>
  );
};

DecoratorWorkOrderDialog.propTypes = PROP_TYPES;

DecoratorWorkOrderDialog.defaultProps = DEFAULT_STATE;

export default DecoratorWorkOrderDialog;
